'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  getTimeRemainingFromDate,
  getTimeRemainingFromUTC,
} from '@/utils/formatters';
import { getTournamentEndInstant } from '@/utils/tournament-end';

interface TournamentCountdownProps {
  signupDeadlineDate: string;
  startDate: string;
  endDate: string;
  /** Close of the tournament's last scheduled match, from the API. */
  endsAt?: string | null;
  onlyFor?: 'tournament' | 'registration',
}

/**
 * Component that displays countdown timers for tournament deadlines
 * Shows registration deadline countdown if tournament hasn't started
 * Shows tournament end countdown if tournament has started but not ended
 */
export function TournamentCountdown({
  signupDeadlineDate,
  startDate,
  endDate,
  endsAt,
  onlyFor
}: TournamentCountdownProps) {
  // Both labels derive from the props and the current time; state exists only
  // to re-render them once a minute.
  const [, tick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => tick((n) => n + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const end = useMemo(
    () => getTournamentEndInstant(endDate, endsAt),
    [endDate, endsAt],
  );

  const now = new Date();
  const tournamentStarted = now >= new Date(startDate);

  const registration =
    onlyFor !== 'tournament' &&
    !tournamentStarted &&
    new Date(signupDeadlineDate) > now
      ? getTimeRemainingFromUTC(signupDeadlineDate)
      : '';

  const tournament =
    onlyFor !== 'registration' && tournamentStarted && end && now < end
      ? getTimeRemainingFromDate(end)
      : '';

  if (!registration && !tournament) {
    return null;
  }

  return (
    <div className="space-y-2">
      {registration && (
        <p className="text-xs font-medium text-amber-600">
          La inscripción cierra {registration}
        </p>
      )}
      {tournament && (
        <p className="text-xs font-medium text-blue-600">
          El torneo finaliza {tournament}
        </p>
      )}
    </div>
  );
}
